import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';

interface Rost { student_id: string; first_name: string; last_name: string; medical_notes: string | null; status: string; }
interface Sess { id: string; date: string; status: string; roster: Rost[]; summary: { attending: number; not_attending: number; absent: number; unmarked: number }; }
interface Cls { id: string; name: string; day_of_week: string; start_time: string; end_time: string; sessions: Sess[]; }

/** Teacher home: the teacher's classes, upcoming sessions with attendance summary,
 *  and an expandable roster where the teacher can mark students absent. */
@customElement('cadence-teacher-dashboard')
export class CadenceTeacherDashboard extends LitElement {
  @state() private classes: Cls[] = [];
  @state() private loading = true;
  @state() private openSession = '';
  @state() private busy = '';

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 4px; font-size: 1.6rem; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); }
    .stack { display: grid; gap: var(--space-4); }
    .session { border: 1px solid var(--color-border); border-radius: var(--radius-md); margin-top: 8px; }
    .session > .row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; cursor: pointer; }
    .session > .row:hover { background: var(--color-bg-secondary); }
    .session.cancelled .row { opacity: 0.6; cursor: default; }
    .date { font-weight: 600; }
    .summary { color: var(--color-text-secondary); font-size: 0.82rem; }
    .roster { border-top: 1px solid var(--color-border); padding: 6px 12px; }
    .student { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--color-border); }
    .student:last-child { border-bottom: none; }
    .who { display: flex; flex-direction: column; }
    .med { color: var(--color-danger); font-size: 0.78rem; }
    .controls { display: flex; gap: 6px; align-items: center; }
    .btn { border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); border-radius: var(--radius-md); padding: 5px 10px; cursor: pointer; font: inherit; font-size: 0.78rem; }
    .btn:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .empty { color: var(--color-text-secondary); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  private async load() {
    this.loading = true;
    try { this.classes = await api.get<Cls[]>('/attendance/my-classes'); }
    finally { this.loading = false; }
  }

  private async mark(session: Sess, student: Rost, status: string) {
    this.busy = `${session.id}:${student.student_id}`;
    try {
      await api.post(`/attendance/${session.id}/${student.student_id}`, { status });
      student.status = status;
      this.recountSummary(session);
      this.classes = [...this.classes];
      showToast(t('teacher.attendanceUpdated'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.busy = ''; }
  }

  private recountSummary(s: Sess) {
    s.summary = {
      attending: s.roster.filter((r) => r.status === 'attending').length,
      not_attending: s.roster.filter((r) => r.status === 'not_attending').length,
      absent: s.roster.filter((r) => r.status === 'absent').length,
      unmarked: s.roster.filter((r) => r.status === 'unmarked').length,
    };
  }

  private statusBadge(status: string) {
    const map: Record<string, [string, string]> = {
      attending: ['var(--color-success)', t('common.portal.attending')],
      not_attending: ['var(--color-text-secondary)', t('common.portal.notAttending')],
      absent: ['var(--color-danger)', t('common.portal.absent')],
      unmarked: ['var(--color-primary)', '—'],
    };
    const [color, label] = map[status] ?? map.unmarked;
    return html`<cadence-badge color=${color}>${label}</cadence-badge>`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const name = store.get().user?.first_name || '';
    return html`
      <cadence-break-banner></cadence-break-banner>
      <h1>${t('auth.dashboard.welcome', { name })}</h1>
      <p class="sub">${t('common.portal.upcomingClasses')}</p>
      ${this.classes.length
        ? html`<div class="stack">${this.classes.map((c) => this.renderClass(c))}</div>`
        : html`<cadence-empty-state heading=${t('teacher.noClasses')}></cadence-empty-state>`}
    `;
  }

  private renderClass(c: Cls) {
    return html`
      <cadence-card heading=${`${c.name} · ${c.day_of_week ?? ''} ${c.start_time ?? ''}`}>
        ${(c.sessions ?? []).map((s) => this.renderSession(s))}
        ${(c.sessions ?? []).length ? '' : html`<p class="empty">${t('teacher.noUpcomingSessions')}</p>`}
      </cadence-card>
    `;
  }

  private renderSession(s: Sess) {
    if (s.status === 'cancelled') {
      return html`<div class="session cancelled"><div class="row"><span class="date">${s.date}</span><cadence-badge color="var(--color-danger)">${t('teacher.statusCancelled')}</cadence-badge></div></div>`;
    }
    const open = this.openSession === s.id;
    return html`
      <div class="session">
        <div class="row" @click=${() => (this.openSession = open ? '' : s.id)}>
          <span class="date">${s.date}</span>
          <span class="summary">${t('teacher.sessionSummary', { attending: s.summary.attending, notAttending: s.summary.not_attending, absent: s.summary.absent })}</span>
        </div>
        ${open ? html`<div class="roster">${s.roster.map((r) => this.renderStudent(s, r))}</div>` : ''}
      </div>
    `;
  }

  private renderStudent(s: Sess, r: Rost) {
    const busy = this.busy === `${s.id}:${r.student_id}`;
    // Can't mark absent someone who already said they won't attend.
    const canMarkAbsent = r.status !== 'not_attending' && r.status !== 'absent';
    return html`
      <div class="student">
        <div class="who">
          <span>${r.first_name} ${r.last_name}</span>
          ${r.medical_notes ? html`<span class="med">⚕ ${r.medical_notes}</span>` : ''}
        </div>
        <div class="controls">
          ${this.statusBadge(r.status)}
          ${canMarkAbsent ? html`<button class="btn" ?disabled=${busy} @click=${() => this.mark(s, r, 'absent')}>${t('teacher.markAbsent')}</button>` : ''}
          ${r.status === 'absent' ? html`<button class="btn" ?disabled=${busy} @click=${() => this.mark(s, r, 'attending')}>${t('teacher.undo')}</button>` : ''}
        </div>
      </div>
    `;
  }
}
